<script setup lang="ts">import { toRef as _toRef, computed as _computed } from 'vue';

import UserAvatarComponent from '@toolify/client/src/modules/entities/user/UserAvatarComponent.vue'
import AutomationAppRoundAvatarComponent from '@toolify/client/src/modules/entities/automation/AutomationAppRoundAvatarComponent.vue'

import {LogItemTitleItemType} from '@toolify/client/src/modules/activity/enum/LogItemTitleItemType'
import {LogTitleItem} from '@toolify/client/src/modules/activity/types/ILogTitleItem'
import {IResponseEntity} from '@toolify/server/src/services/EntityService/types/IResponseEntity'
import {
  IResponseResourcePath,
} from '@toolify/server/src/services/ResourceService/types/resourcePath/IResponseResourcePath'
import {RouteLocationRaw, useRouter} from 'vue-router'
import {useThemeVariables} from '@toolify/client/src/modules/theme/composables/useThemeVariables'
import {getEntityNameByEntity} from '@toolify/client/src/modules/entities/functions/getEntityNameByEntity'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {ColorVariantType} from '../../ui/enum/ColorVariantType'
import {StyleVariantType} from '../../ui/enum/StyleVariantType'
import {SizeVariantType} from '../../ui/enum/SizeVariantType'
import IconComponent from '../../ui/components/IconComponent.vue'
import ButtonComponent from '../../ui/components/ButtonComponent.vue'
import {IconWeightType} from '../../ui/enum/IconWeightType'
import EllipsisFloatingTooltipComponent from '../../modals/components/EllipsisFloatingTooltipComponent.vue'
import {useDateFormat} from '@toolify/client/src/modules/date/composables/useDateFormat'
import NotificationItemContentTypeFactory from '../Factory/NotificationItemContentTypeFactory.vue'
import {NotificationItemContentType} from '../enum/NotificationItemContentType'
import FloatingTooltipComponent from '../../modals/components/FloatingTooltipComponent.vue'
import {useResponsive} from '@toolify/client/src/composables/useResponsive'
import BottomSheetComponent from '../../modals/components/BottomSheetComponent.vue'
import {useTouchGestures} from '@toolify/client/src/composables/useTouchGestures'
import {defineAsyncComponent, ref} from 'vue'
import {useI18n} from 'vue-i18n'
import {useWorkspaceApiStore} from '@toolify/client/src/stores/api/WorkspaceApiStore/useWorkspaceApiStore'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {WorkspacePermissionRequestStatus} from '@toolify/server/src/models/mongoose/WorkspacePermissionRequestModel/enum/WorkspacePermissionRequestStatus'
import {WorkspaceAppAccessRequestStatus} from '@toolify/server/src/models/mongoose/WorkspaceAppAccessRequestModel/enum/WorkspaceAppAccessRequestStatus'
import {useMessages} from '@toolify/client/src/modules/notifications/composables/useMessages'
import EntityWithStatusComponent from '@toolify/client/src/modules/entities/components/EntityWithStatusComponent.vue'
import {getViaEntityAppPayload} from '@toolify/client/src/modules/entities/functions/getViaEntityAppPayload'
import {getViaEntityLabelKey} from '@toolify/client/src/modules/entities/functions/getViaEntityLabelKey'
import {
  IWorkspacePermissionRequestRequestedForResponseEntity,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IWorkspacePermissionRequestRequestedForResponseEntity'
import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'
import {useMessageApiStore} from '@toolify/client/src/stores/api/MessageApiStore/useMessageApiStore'
import {INotificationItem} from '@toolify/client/src/modules/notifications/types/INotificationItem'

// Async: the history surfaces render event rows with this very component
const NotificationGroupHistoryModal = defineAsyncComponent(() => import('./NotificationGroupHistoryModal.vue'))
const NotificationGroupHistoryContent = defineAsyncComponent(() => import('./NotificationGroupHistoryContent.vue'))

interface Props {
  titleItems: LogTitleItem[];
  primaryIconClass: IconNameType;
  secondaryIconClass: string;
  authorEntity: IResponseEntity;
  viaEntity?: IResponseEntity | null;
  viaEntityLabel?: string | null;
  contentType?: NotificationItemContentType;
  createdAt: Date;
  isRead?: boolean;
  contentPayload?: unknown;
  to?: RouteLocationRaw | null;
  resourceName?: string;
  isAggregationGroup?: boolean;
  isSelected?: boolean;
  showDismissButton?: boolean;
  // Read-only rendering for the group-history view: no actions, no read state, no navigation
  isPreview?: boolean;
  // When set (a grouped notification), a history button opens the group's event preview
  groupHistoryNotificationId?: string;
  // The group head's path, lent to the slim history events so their titles can name the item
  groupHistoryResourcePath?: IResponseResourcePath;
  groupHistoryEventCount?: number;
  workspaceAppAccessPayload?: {
    requestId: string
    appName: string
    workspaceName: string
    requesterName: string
    status?: WorkspaceAppAccessRequestStatus | null
    reviewedByEntity?: IResponseEntity | null
  } | null;
  aiApprovalPayload?: INotificationItem['aiApprovalPayload'];
  workspacePermissionRequestPayload?: {
    requestId: string
    requestedFor: IWorkspacePermissionRequestRequestedForResponseEntity | null
    workspaceName: string
    authorEntity: IResponseEntity
    viaEntity: IResponseEntity | null
    permissionType: string
    status: string
    reviewedByEntity: IResponseEntity | null
  } | null;
}
const props = withDefaults(defineProps<Props>(), {
  secondaryIconBorderColor: null,
  isRead: undefined,
  canBeReverted: false,
  hasBeenReverted: false,
  resourceName: null,
  contentType: undefined,
  contentPayload: undefined,
  isAggregationGroup: false,
  isSelected: false,
  showDismissButton: false,
  isPreview: false,
  groupHistoryNotificationId: null,
  groupHistoryResourcePath: null,
  groupHistoryEventCount: null,
  workspaceAppAccessPayload: null,
  aiApprovalPayload: null,
  workspacePermissionRequestPayload: null,
  viaEntity: null,
  viaEntityLabel: null,
})

interface Emits {
  (e: 'read'): void;
  (e: 'unread'): void;
  (e: 'revert'): void;
  (e: 'dismiss'): void;
}
const emit = defineEmits<Emits>()

const router = useRouter()
const __$temp_1 = (useThemeVariables()),
  getThemeVariable = _toRef(__$temp_1, 'getThemeVariable');
const {t} = useI18n()

const shouldClampNotificationTitleItem = _computed(() => {
  return getThemeVariable.value(ThemeVariableType.ShouldClampNotificationTitleItem)
})
const notificationIconWeightType = _computed(() => {
  return getThemeVariable.value(ThemeVariableType.NotificationIconWeightType)
})
const showNotificationResourceSpace = _computed(() => {
  return getThemeVariable.value(ThemeVariableType.ShowNotificationResourceSpace)
})

const {formatDisplayDate} = useDateFormat()

const formattedDate = _computed(() => {
  return formatDisplayDate(props.createdAt)
})

const viaEntityApp = _computed(() => {
  return props.viaEntity ? getViaEntityAppPayload(props.viaEntity) : null
})

const viaEntityName = _computed(() => {
  return getEntityNameByEntity(props.viaEntity) || viaEntityApp.value?.name || null
})

const {canHover} = useResponsive()

const isBottomSheetOpen = ref(false)
const isLongPressing = ref(false)
let longPressTimeout: ReturnType<typeof setTimeout> | null = null
let visualFeedbackTimeout: ReturnType<typeof setTimeout> | null = null
const LONG_PRESS_DURATION = 500
const VISUAL_FEEDBACK_DELAY = 250

const {
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  isDragging,
} = useTouchGestures({
  onStart: () => {
    if (props.isPreview) {
      return
    }
    visualFeedbackTimeout = setTimeout(() => {
      isLongPressing.value = true
    }, VISUAL_FEEDBACK_DELAY)
    
    longPressTimeout = setTimeout(() => {
      isBottomSheetOpen.value = true
    }, LONG_PRESS_DURATION)
  },
  onMove: (deltaX, deltaY) => {
    if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
      if (visualFeedbackTimeout) {
        clearTimeout(visualFeedbackTimeout)
        visualFeedbackTimeout = null
      }
      if (longPressTimeout) {
        clearTimeout(longPressTimeout)
        longPressTimeout = null
      }
      isLongPressing.value = false
    }
  },
  onEnd: () => {
    if (visualFeedbackTimeout) {
      clearTimeout(visualFeedbackTimeout)
      visualFeedbackTimeout = null
    }
    if (longPressTimeout) {
      clearTimeout(longPressTimeout)
      longPressTimeout = null
    }
    isLongPressing.value = false
  },
})

const onRead = () => {
  emit('read')
  isBottomSheetOpen.value = false
}

const onUnread = () => {
  emit('unread')
  isBottomSheetOpen.value = false
}

// Mobile path to the group history: the long-press action sheet swaps into a full bottom sheet
// (the inline trigger button is desktop-only — too small a touch target)
const isHistorySheetOpen = ref(false)
const onShowHistory = () => {
  isBottomSheetOpen.value = false
  isHistorySheetOpen.value = true
}

const workspaceApiStore = useWorkspaceApiStore()
const authStore = useAuthStore()

const isWprPending = _computed(() => {
  return props.workspacePermissionRequestPayload?.status === WorkspacePermissionRequestStatus.Pending
    || !props.workspacePermissionRequestPayload?.status
})

const isWprReviewedByCurrentUser = _computed(() => {
  const reviewedBy = props.workspacePermissionRequestPayload?.reviewedByEntity
  const currentUserId = authStore.currentEntityId
  return !!reviewedBy
    && reviewedBy.entityType === EntityType.User
    && reviewedBy.entityId === currentUserId
})

const wprResolvedLabel = _computed(() => {
  if(!props.workspacePermissionRequestPayload) {
    return ''
  }
  if(props.workspacePermissionRequestPayload.status === WorkspacePermissionRequestStatus.Rejected) {
    return isWprReviewedByCurrentUser.value
      ? t('notifications.workspacePermissionRequest.rejectedByYou')
      : t('notifications.workspacePermissionRequest.rejectedBy')
  }
  return isWprReviewedByCurrentUser.value
    ? t('notifications.workspacePermissionRequest.acceptedByYou')
    : t('notifications.workspacePermissionRequest.acceptedBy')
})

const messages = useMessages()

const isWapPending = _computed(() => {
  return props.workspaceAppAccessPayload?.status === WorkspaceAppAccessRequestStatus.Pending
    || !props.workspaceAppAccessPayload?.status
})

const isWapReviewedByCurrentUser = _computed(() => {
  const reviewedBy = props.workspaceAppAccessPayload?.reviewedByEntity
  return !!reviewedBy
    && reviewedBy.entityType === EntityType.User
    && reviewedBy.entityId === authStore.currentEntityId
})

const wapResolvedLabel = _computed(() => {
  const status = props.workspaceAppAccessPayload?.status
  if(status === WorkspaceAppAccessRequestStatus.Cancelled) {
    return t('oauth.workspaceAppAccessModal.cancelled')
  }
  if(status === WorkspaceAppAccessRequestStatus.Rejected) {
    return isWapReviewedByCurrentUser.value
      ? t('notifications.workspacePermissionRequest.rejectedByYou')
      : t('notifications.workspacePermissionRequest.rejectedBy')
  }
  return isWapReviewedByCurrentUser.value
    ? t('notifications.workspacePermissionRequest.acceptedByYou')
    : t('notifications.workspacePermissionRequest.acceptedBy')
})

const wapAction = ref<'approve' | 'reject' | null>(null)
const isWapBusy = _computed(() => wapAction.value !== null)

async function onWapApprove(): Promise<void> {
  if(!props.workspaceAppAccessPayload || wapAction.value) {
    return
  }
  wapAction.value = 'approve'
  try {
    await workspaceApiStore.approveWorkspaceAppAccessRequest(props.workspaceAppAccessPayload.requestId)
    messages.success(t('settings.appInstalledSuccess'))
  } catch {
    messages.error(t('oauth.workspaceAppAccessModal.approveFailed'))
  } finally {
    wapAction.value = null
  }
}

async function onWapReject(): Promise<void> {
  if(!props.workspaceAppAccessPayload || wapAction.value) {
    return
  }
  wapAction.value = 'reject'
  try {
    await workspaceApiStore.rejectWorkspaceAppAccessRequest(props.workspaceAppAccessPayload.requestId)
    messages.success(t('oauth.workspaceAppAccessModal.rejectedSuccess'))
  } catch {
    messages.error(t('oauth.workspaceAppAccessModal.rejectFailed'))
  } finally {
    wapAction.value = null
  }
}

const wprAction = ref<'approve' | 'reject' | null>(null)
const isWprBusy = _computed(() => wprAction.value !== null)

async function onWprApprove(): Promise<void> {
  if(!props.workspacePermissionRequestPayload || wprAction.value) {
    return
  }
  wprAction.value = 'approve'
  try {
    await workspaceApiStore.approveWorkspacePermissionRequest(props.workspacePermissionRequestPayload.requestId)
  } finally {
    wprAction.value = null
  }
}

async function onWprReject(): Promise<void> {
  if(!props.workspacePermissionRequestPayload || wprAction.value) {
    return
  }
  wprAction.value = 'reject'
  try {
    await workspaceApiStore.rejectWorkspacePermissionRequest(props.workspacePermissionRequestPayload.requestId)
  } finally {
    wprAction.value = null
  }
}

const messageApiStore = useMessageApiStore()
const aiApprovalAction = ref<'approve' | 'reject' | null>(null)
const isAiApprovalGroup = _computed(() => (props.aiApprovalPayload?.pendingActionCount ?? 1) > 1)

async function resolveAiApproval(action: 'approve' | 'reject'): Promise<void> {
  if(!props.aiApprovalPayload || aiApprovalAction.value) {
    return
  }
  aiApprovalAction.value = action
  try {
    await messageApiStore.resolveLlmToolUsed(props.aiApprovalPayload, action)
    emit('read')
  } finally {
    aiApprovalAction.value = null
  }
}
</script>

<template>
  <div
    class="notification-item"
    :class="{ 'is-unread': !isRead, 'has-content': !!contentType, 'is-active': isLongPressing || isBottomSheetOpen || isSelected, 'is-preview': isPreview }"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
  >
    <div class="avatar-wrapper">
      <div class="avatar-background">
        <UserAvatarComponent :entity="authorEntity" :alt="$t('userSettings.avatar')"/>
        <div class="icon-wrapper" :class="secondaryIconClass">
          <IconComponent
            :name="primaryIconClass"
            :size="SizeVariantType.XSmall"
            :weightType="notificationIconWeightType"
          />
        </div>
      </div>
    </div>
    <div class="content">
      <div
        class="title"
        :class="{
          read: isRead,
          'title--flow': !shouldClampNotificationTitleItem,
        }"
      >
        <template v-for="(titleItem, i) in titleItems" :key="i">
          <span
            class="string"
            v-if="titleItem.type === LogItemTitleItemType.String"
          >{{titleItem.payload}}</span>
          <span
            class="link"
            v-else-if="titleItem.type === LogItemTitleItemType.Link"
          >{{titleItem.payload.text}}</span>
          <span
            class="author"
            v-else-if="titleItem.type === LogItemTitleItemType.Entity"
          >
            {{getEntityNameByEntity(titleItem.payload)}}
          </span>
          <div
            class="item"
            :class="{'item--clamped': shouldClampNotificationTitleItem}"
            v-else-if="titleItem.type === LogItemTitleItemType.Item"
          >
            <div class="item-icon-wrapper" v-if="titleItem.payload.iconNameType">
              <IconComponent
                :name="titleItem.payload.iconNameType"
                :size="SizeVariantType.XSmall"
                :weightType="notificationIconWeightType"
              />
            </div>
            <div class="item-name-wrapper">
              <EllipsisFloatingTooltipComponent
                v-if="shouldClampNotificationTitleItem"
                :class="{ read: isRead }"
              >
                {{titleItem.payload.text}}
              </EllipsisFloatingTooltipComponent>
              <span v-else :class="{ read: isRead }">{{titleItem.payload.text}}</span>
            </div>
          </div>
        </template>
        <span v-if="viaEntity" class="via-entity">
          <span class="via-entity__label">{{viaEntityLabel || t(getViaEntityLabelKey(viaEntity))}}</span>
          <FloatingTooltipComponent placement="top" class="via-entity__avatar-wrap">
            <AutomationAppRoundAvatarComponent v-if="viaEntityApp" :app="viaEntityApp" :sizeRem="0.875"/>
            <UserAvatarComponent v-else :entity="viaEntity" alt=""/>
            <template #tooltip>{{viaEntityName}}</template>
          </FloatingTooltipComponent>
          <EllipsisFloatingTooltipComponent
            v-if="shouldClampNotificationTitleItem"
            class="via-entity__name"
          >
            {{viaEntityName}}
          </EllipsisFloatingTooltipComponent>
          <span v-else class="via-entity__name">{{viaEntityName}}</span>
        </span>
      </div>
      <div
        class="payload"
        :class="{ read: isRead }"
        v-if="contentType"
      >
        <NotificationItemContentTypeFactory
          :payload="contentPayload"
          :contentType="contentType"
          :createdAt="props.createdAt"
        />
      </div>
      <div
        v-if="workspaceAppAccessPayload && isWapPending"
        class="wap-inline-actions"
        @click.stop
      >
        <ButtonComponent
          :size="SizeVariantType.Small"
          :color="ColorVariantType.Neutral"
          :style="StyleVariantType.FilledFilled"
          :isLoading="wapAction === 'reject'"
          :isDisabled="isWapBusy"
          @click.stop="onWapReject"
        >
          {{t('oauth.reject')}}
        </ButtonComponent>
        <ButtonComponent
          :size="SizeVariantType.Small"
          :color="ColorVariantType.Primary"
          :style="StyleVariantType.FilledFilled"
          :isLoading="wapAction === 'approve'"
          :isDisabled="isWapBusy"
          @click.stop="onWapApprove"
        >
          {{t('general.accept')}}
        </ButtonComponent>
      </div>
      <div
        v-else-if="workspaceAppAccessPayload"
        class="wpr-resolved"
        @click.stop
      >
        <span class="wpr-resolved-label">{{wapResolvedLabel}}</span>
        <EntityWithStatusComponent
          v-if="!isWapReviewedByCurrentUser && workspaceAppAccessPayload.reviewedByEntity"
          :entity="workspaceAppAccessPayload.reviewedByEntity"
          class="wpr-resolved-entity"
        />
      </div>
      <div
        v-if="aiApprovalPayload && !isRead && !isPreview"
        class="wap-inline-actions"
        @click.stop
      >
        <ButtonComponent
          :size="SizeVariantType.Small"
          :color="ColorVariantType.Primary"
          :style="StyleVariantType.FilledFilled"
          :iconNameType="IconNameType.Check"
          :isLoading="aiApprovalAction === 'approve'"
          :isDisabled="aiApprovalAction !== null"
          @click.stop="resolveAiApproval('approve')"
        >
          {{isAiApprovalGroup ? t('chat.llmToolApproveAll') : t('chat.llmToolAllowOnce')}}
        </ButtonComponent>
        <ButtonComponent
          :size="SizeVariantType.Small"
          :color="ColorVariantType.Neutral"
          :style="StyleVariantType.FilledFilled"
          :iconNameType="IconNameType.Xmark"
          :isLoading="aiApprovalAction === 'reject'"
          :isDisabled="aiApprovalAction !== null"
          @click.stop="resolveAiApproval('reject')"
        >
          {{isAiApprovalGroup ? t('chat.llmToolRejectAll') : t('chat.llmToolReject')}}
        </ButtonComponent>
      </div>
      <div
        v-if="workspacePermissionRequestPayload && isWprPending"
        class="wap-inline-actions"
        @click.stop
      >
        <ButtonComponent
          :size="SizeVariantType.Small"
          :color="ColorVariantType.Primary"
          :style="StyleVariantType.FilledFilled"
          :isLoading="wprAction === 'approve'"
          :isDisabled="isWprBusy"
          @click.stop="onWprApprove"
        >
          {{t('general.accept')}}
        </ButtonComponent>
        <ButtonComponent
          :size="SizeVariantType.Small"
          :color="ColorVariantType.Neutral"
          :style="StyleVariantType.FilledFilled"
          :isLoading="wprAction === 'reject'"
          :isDisabled="isWprBusy"
          @click.stop="onWprReject"
        >
          {{t('oauth.reject')}}
        </ButtonComponent>
      </div>
      <div
        v-else-if="workspacePermissionRequestPayload && !isWprPending"
        class="wpr-resolved"
        @click.stop
      >
        <span class="wpr-resolved-label">{{wprResolvedLabel}}</span>
        <EntityWithStatusComponent
          v-if="!isWprReviewedByCurrentUser && workspacePermissionRequestPayload.reviewedByEntity"
          :entity="workspacePermissionRequestPayload.reviewedByEntity"
          class="wpr-resolved-entity"
        />
      </div>
      <div class="additional">
        <div class="date">
          <IconComponent
            :name="IconNameType.Clock"
            :weightType="notificationIconWeightType"
          />
          {{formattedDate}}
        </div>
        <div class="dot" v-if="showNotificationResourceSpace && resourceName">&middot;</div>
        <div class="space" v-if="showNotificationResourceSpace && resourceName">
          <IconComponent
            :name="IconNameType.DiagramSubtask"
            :weightType="notificationIconWeightType"
          />
          {{resourceName}}
        </div>
      </div>
    </div>

    <div class="action-container" @click.prevent.stop v-if="!isPreview">
      <NotificationGroupHistoryModal
        :notificationId="groupHistoryNotificationId"
        :resourcePath="groupHistoryResourcePath"
        :eventCount="groupHistoryEventCount"
        v-if="groupHistoryNotificationId && canHover"
      />
      <FloatingTooltipComponent placement="top" v-if="canHover">
        <div 
          class="read-toggle-button" 
          :class="{ 'is-unread': !isRead, 'is-read': isRead, 'is-aggregation-group': isAggregationGroup }"
          @click.stop="isRead ? emit('unread') : emit('read')"
        >
          <div class="dot-inner"/>
        </div>
        <template #tooltip>
          {{!isRead ? t('notifications.tooltips.markAsRead') : t('notifications.tooltips.markAsUnread')}}
        </template>
      </FloatingTooltipComponent> 
      <div 
        v-else
        class="read-toggle-button" 
        :class="{ 'is-unread': !isRead, 'is-read': isRead, 'is-aggregation-group': isAggregationGroup }"
        @click.stop="isRead ? emit('unread') : emit('read')"
      >
        <div class="dot-inner"/>
      </div>
      <FloatingTooltipComponent placement="top" v-if="showDismissButton && canHover">
        <div
          class="action-icon-button"
          @click.stop="emit('dismiss')"
        >
          <IconComponent
            :name="IconNameType.Xmark"
            :size="SizeVariantType.XSmall"
            :weightType="IconWeightType.Solid"
          />
        </div>
        <template #tooltip>
          {{$t('files.dismissFailedTransfer')}}
        </template>
      </FloatingTooltipComponent>
      <div
        v-else-if="showDismissButton"
        class="action-icon-button"
        @click.stop="emit('dismiss')"
      >
        <IconComponent
          :name="IconNameType.Xmark"
          :size="SizeVariantType.XSmall"
        />
      </div>
    </div>
    <BottomSheetComponent
      :isOpen="isBottomSheetOpen"
      @hide="isBottomSheetOpen = false"
      :shouldShowHeader="false"
      :isMaxHeightOnAutoMode="false"
      :allowResize="false"
      v-if="!canHover && isBottomSheetOpen && !isPreview"
    >
      <template #sheet>
        <div class="bottom-sheet-actions">
          <ButtonComponent
            :iconNameType="IconNameType.AlarmSnooze"
            :color="ColorVariantType.Neutral"
            :style="StyleVariantType.FilledFilled"
            :size="SizeVariantType.Large"
            class="action-button"
            v-if="false"
          >
            {{t('notifications.tooltips.snooze')}}
          </ButtonComponent>
          <ButtonComponent
            :iconNameType="IconNameType.Eye"
            :color="ColorVariantType.Neutral"
            :style="StyleVariantType.FilledFilled"
            :size="SizeVariantType.Large"
            @click.stop="onRead"
            v-if="!isRead"
            class="action-button"
          >
            {{t('notifications.tooltips.markAsRead')}}
          </ButtonComponent>
          <ButtonComponent
            :iconNameType="IconNameType.EyeSlash"
            :color="ColorVariantType.Neutral"
            :style="StyleVariantType.FilledFilled"
            :size="SizeVariantType.Large"
            @click.stop="onUnread"
            v-else
            class="action-button"
          >
            {{t('notifications.tooltips.markAsUnread')}}
          </ButtonComponent>
          <ButtonComponent
            :iconNameType="IconNameType.ClockRotateLeft"
            :color="ColorVariantType.Neutral"
            :style="StyleVariantType.FilledFilled"
            :size="SizeVariantType.Large"
            @click.stop="onShowHistory"
            v-if="groupHistoryNotificationId"
            class="action-button"
          >
            {{t('notifications.groupHistory.title')}}
          </ButtonComponent>
        </div>
      </template>
    </BottomSheetComponent>
    <BottomSheetComponent
      :isOpen="isHistorySheetOpen"
      @hide="isHistorySheetOpen = false"
      :shouldShowHeader="false"
      v-if="!canHover && isHistorySheetOpen"
    >
      <template #sheet>
        <NotificationGroupHistoryContent
          :notificationId="groupHistoryNotificationId"
          :resourcePath="groupHistoryResourcePath"
          :eventCount="groupHistoryEventCount"
          :showCloseButton="false"
          :isFullHeight="true"
          class="history-sheet-content"
        />
      </template>
    </BottomSheetComponent>
  </div>
</template>

<style scoped lang="scss">
@use "../../../styles/variables" as variables;
@use '@toolify/client/src/styles/light' as light;

.notification-item {
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-rows: minmax(38px, auto);
  grid-template-areas: "avatar content action-container";
  column-gap: map-get(variables.$spacing, space-2);
  position: relative;
  cursor: pointer;
  padding: map-get(variables.$spacing, space-3);
  border-radius: variables.$borderRadius;

  background: transparent;
  transition: all 0.2s ease;

  // light: hover i aktywna pozycja to nakladka, bo pozycja lezy na bialym menu dzwonka, na karcie popupu
  // albo w ciemnym pasku bocznym (tam nakladka jest biela z alfa)
  &:not(.is-unread) {
    opacity: 0.55;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: map-get(variables.$colors, container-lowest);
        opacity: 0.8;
        @include light.hover;
      }
    }

    &.is-active {
      background: map-get(variables.$colors, container-lowest);
      opacity: 0.8;
      @include light.hover;
    }
  }

  &.is-unread {
    opacity: 1;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: map-get(variables.$colors, container-low);
        @include light.hover;
      }
    }

    &.is-active {
      background: map-get(variables.$colors, container-low);
      @include light.hover;
    }
  }

  &.has-content {
    grid-template-rows: minmax(38px, auto) 1fr;
    grid-template-areas: "avatar content action-container";
  }

  &.is-preview {
    cursor: default;
  }
}

.action-container {
  grid-area: action-container;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: map-get(variables.$spacing, space-0-5);
  position: relative;
  min-width: fit-content;
}

.read-toggle-button,
.action-icon-button {
  width: map-get(variables.$sizes, size-lg);
  height: map-get(variables.$sizes, size-lg);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: transparent;

  :deep(.icon) {
    color: map-get(variables.$colors, content-dim);
  }

  &:hover {
    color: map-get(variables.$colors, content-normal);
  }

  .dot-inner {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    box-sizing: border-box;
  }

  &.is-read .dot-inner {
    border: 1.5px solid map-get(variables.$colors, content-dark);
    background: transparent;
  }

  &.is-unread .dot-inner {
    border: none;
    background: map-get(variables.$colors, primary);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: map-get(variables.$colors, container-highest);
    }
  }
}

.avatar-background {
  position: relative;
  display: flex;
  width: fit-content;
  height: fit-content;
}

.avatar-wrapper {
  grid-area: avatar;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  position: relative;
  i {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    line-height: 100%;
    right: -4px;
    bottom: -1px;
    width: map-get(variables.$sizes, size-xs);
    height: map-get(variables.$sizes, size-xs);
    font-size: map-get(variables.$textSizes, text-xs);
    border-radius: map-get(variables.$radiuses, radius-xs);
    background: map-get(variables.$colors, container-high);
    color: map-get(variables.$colors, content-light);
  }
}

.additional {
  color: map-get(variables.$colors, content-dark);
  grid-area: additional;
  display: flex;
  gap: map-get(variables.$spacing, space-1-5);
  font-size: map-get(variables.$textSizes, text-xs);
  align-items: center;
  align-self: flex-start;

  :deep(.icon) {
    color: map-get(variables.$colors, content-dark);
    height: 12px;
  }
}

.date {
  display: flex;
  align-items: center;
  gap: map-get(variables.$spacing, space-1);
  white-space: nowrap;
}
.dot {
  display: flex;
}
.space {
  display: flex;
  align-items: center;
  gap: map-get(variables.$spacing, space-1);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.payload {
  cursor: pointer;
  grid-area: payload;
  overflow: hidden;
  :deep(.text-wrapper) {
    color: map-get(variables.$colors, content-normal);
  }
}

.line-wrapper {
  width: 2px;
  background: map-get(variables.$colors, overlay-10);
  height: 100%;
  grid-area: line;
  justify-self: center;
  border-radius: map-get(variables.$radiuses, radius-lg);

  .notification-item:last-child & {
    display: none;
  }
}

.content {
  grid-area: content;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  display: flex;
  flex-direction: column;
  gap: map-get(variables.$spacing, space-1);
}

.content .title {
  display: flex;
  flex-wrap: wrap;
  gap: map-get(variables.$spacing, space-1);
  align-items: center;

  // dark / light: wrap like a sentence so long Item labels can continue the previous line
  &.title--flow {
    display: block;
    line-height: 1.4;

    > .author,
    > .string,
    > .link,
    > .item,
    > .via-entity {
      margin-right: 4px;
    }

    > .author {
      display: inline;
      white-space: nowrap;
    }

    > .string,
    > .link {
      display: inline;
      white-space: normal;
      text-wrap: wrap;
    }

    > .item {
      display: inline;
      vertical-align: baseline;

      &:has(.item-icon-wrapper) {
        display: inline-flex;
        align-items: center;
        gap: map-get(variables.$spacing, space-1);
        max-width: 100%;
        white-space: nowrap;
        vertical-align: middle;
      }

      .item-name-wrapper {
        display: inline;
        overflow: visible;
      }
    }

    > .via-entity {
      display: inline-flex;
      vertical-align: middle;
      margin-left: 0;
    }
  }
}

.via-entity {
  display: inline-flex;
  align-items: center;
  gap: map-get(variables.$spacing, space-1);
  min-width: 0;
  max-width: 100%;
  margin-left: 2px;
  color: map-get(variables.$colors, content-dark);
}

.via-entity__label {
  flex-shrink: 0;
  text-transform: lowercase;
}

.via-entity__avatar-wrap {
  flex-shrink: 0;
  display: flex;
  border-radius: 50%;
}

.via-entity__name {
  min-width: 0;
  font-weight: 500;
  color: map-get(variables.$colors, content-normal);
}

.wap-inline-actions {
  display: flex;
  flex-wrap: wrap;
  gap: map-get(variables.$spacing, space-2);
  margin-top: 4px;
}

.wpr-resolved {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 4px;
  font-size: map-get(variables.$textSizes, text-sm);
  color: map-get(variables.$colors, content-dim);
}

.wpr-resolved-entity {
  min-width: 0;
}

.user-avatar {
  height: map-get(variables.$sizes, size-lg);
}

.icon-wrapper {
  position: absolute;
  border-radius: map-get(variables.$radiuses, radius-full);
  width: 1rem;
  height: 1rem;
  bottom: -0.25rem;
  right: -4px;
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 100%;
  background: map-get(variables.$colors, container-highest);
  :deep(.icon) {
    font-size: map-get(variables.$textSizes, text-3xs);
    color: map-get(variables.$colors, content-light);
  }
}

.bg-red {
  background: map-get(variables.$colors, error);
}

.bg-blue {
  background: map-get(variables.$colors, container-high);
}

.bg-green {
  background: map-get(variables.$colors, primary);
}

.string {
  color: map-get(variables.$colors, content-dark);
  text-wrap: nowrap;
}

.author {
  font-weight: 500;
  text-wrap: nowrap;
}

.link {
  font-weight: 600;
}

.item {
  color: map-get(variables.$colors, content-normal);
  display: grid;
  align-items: center;
  gap: map-get(variables.$spacing, space-1);
  box-sizing: content-box;
  line-height: unset;
  grid-template-columns: auto 1fr;

  &:not(:has(.item-icon-wrapper)) {
    grid-template-columns: 1fr;
  }

  &.item--clamped {
    max-width: 15ch;
  }

  .item-name-wrapper {
    overflow: hidden;
  }

  .item-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    :deep(.icon) {
      color: map-get(variables.$colors, content-normal);
      font-size: map-get(variables.$textSizes, text-sm);
    }
  }

}


.bottom-sheet-actions {
  display: flex;
  flex-direction: column;
  gap: map-get(variables.$spacing, space-2);
  width: 100%;
  padding: map-get(variables.$spacing, space-4);
}

.history-sheet-content {
  width: 100%;
  height: 100%;
}

.action-button {
  width: 100%;
  justify-content: flex-start;
}
</style>
